<template>
  <div class="foryou-container" :class="{ 'has-content': users.length > 0, 'is-grid-mode': viewMode === 'grid' }">
    <!-- Header with Title -->
    <div v-if="users.length > 0" class="foryou-header">
      <div class="foryou-title-block">
        <h2 class="foryou-title">
          <span class="sparkle-icon">✨</span> {{ $t('Profiles for you') }}!
        </h2>
        <p class="foryou-subtitle">{{ $t('A simple hello can start everything') }}.</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && users.length === 0" class="loading-state">
      <loading-spinner />
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && users.length === 0" class="empty-state">
      <img :src="emptyImage" alt="" class="empty-image">
      <h3 class="empty-title">{{ $t('No profiles yet') }}</h3>
      <p class="empty-subtitle">{{ $t('Start discovering new people now') }}</p>
      <button class="discover-btn" @click="goToDiscover">
        {{ $t('Discover') }}
      </button>
      <div class="create-post-btn" @click="$emit('open-new-post')">
        <div class="outer-ring">
          <div class="inner-ring">
            <font-awesome-icon :icon="['fas', 'plus']" />
          </div>
        </div>
      </div>
    </div>

    <!-- 1. GRID VIEW (Modern Desktop & Responsive Grid) -->
    <!-- Sadece ızgara görünümü (kart/kaydırma görünümü kaldırıldı) -->
    <div v-else class="foryou-grid-wrapper">
      <div class="foryou-grid">
        <div
          v-for="(user, idx) in users"
          :key="'grid-' + (user.id || user.uuid || idx)"
          class="grid-card-item"
        >
          <div class="grid-card-inner">
            <!-- Media (Image or Video) -->
            <div class="grid-card-media-wrapper">
              <div v-if="!user.mediaLoaded" class="media-skeleton">
                <div class="shimmer"></div>
              </div>

              <img
                v-if="user.media_type !== 'video'"
                v-show="user.mediaLoaded"
                :src="user.media_url || user.profile_photo_url"
                class="grid-card-media"
                alt=""
                draggable="false"
                @load="onMediaLoad(user)"
                @error="onMediaLoad(user)"
              >

              <video
                v-else
                :ref="`grid-video-${idx}`"
                v-show="user.mediaLoaded"
                :src="user.media_url"
                class="grid-card-media is-video grid-autoplay-video"
                loop
                playsinline
                webkit-playsinline
                muted
                autoplay
                preload="metadata"
                @loadeddata="onGridVideoLoaded(user, $event)"
                @error="onMediaLoad(user)"
              />
            </div>

            <!-- Top Header Overlay -->
            <div class="grid-card-header">
              <div class="user-meta" @click.stop="goToProfile(user)">
                <b-avatar
                  size="38"
                  :src="user.profile_photo_url"
                  variant="light-primary"
                  class="user-avatar"
                />
                <div class="user-text">
                  <div class="user-name-row">
                    <span class="user-name">{{ user.user_name }}, {{ user.age }}</span>
                  </div>
                  <span class="content-type-badge" :class="user.is_ai ? 'ai' : 'real'">
                    {{ user.is_ai ? 'AI' : 'Real' }}
                  </span>
                </div>
              </div>

              <!-- 3 Dots Options Button -->
              <button
                class="card-options-btn"
                @click.stop="openUserActionMenu(user)"
                :title="$t('Options') || 'Seçenekler'"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2.2"></circle>
                  <circle cx="12" cy="12" r="2.2"></circle>
                  <circle cx="12" cy="19" r="2.2"></circle>
                </svg>
              </button>
            </div>

            <!-- Bottom Action Footer: Keşfet kartlarındaki butonların aynısı (arama / sohbet / beğeni) -->
            <div class="grid-card-footer">
              <user-card-actions
                :user="user"
                call-source="for_you"
                class="flex-grow-1"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="!allLoaded" class="load-more-section">
        <button
          class="load-more-btn"
          :disabled="loading"
          @click="loadMore"
        >
          <span v-if="loading">{{ $t('Loading') || 'Yükleniyor...' }}</span>
          <span v-else>{{ $t('Discover More') || 'Daha Fazla Keşfet' }} ↓</span>
        </button>
      </div>
    </div>

    <!-- Action Modal for Profile -->
    <b-modal
      v-model="actionModalVisible"
      modal-class="actions-modal"
      centered
      hide-header
      hide-footer
      content-class="actions-modal-content"
    >
      <div v-if="selectedUserForAction" class="actions-list">
        <div class="action-item" @click="goToProfile(selectedUserForAction)">
          <span class="action-icon">👤</span> {{ $t('View profile') }}
        </div>
        <div class="action-item" @click="goToChat(selectedUserForAction)">
          <span class="action-icon">💬</span> {{ $t('Send Message') || 'Mesaj Gönder' }}
        </div>
        <div class="action-item" @click="copyUserLink(selectedUserForAction)">
          <span class="action-icon">🔗</span> {{ $t('Copy Link') || 'Bağlantıyı Kopyala' }}
        </div>
        <div class="action-item text-danger" @click="blockUser(selectedUserForAction)">
          <span class="action-icon">🚫</span> {{ $t('Block') }}
        </div>
        <div class="action-item text-warning" @click="openReport(selectedUserForAction)">
          <span class="action-icon">⚠️</span> {{ $t('Report') }}
        </div>
        <div class="action-item cancel" @click="actionModalVisible = false">
          {{ $t('Cancel') }}
        </div>
      </div>
    </b-modal>

    <!-- Şikayet et (profil sayfasındaki ile aynı servis: reportUser2) -->
    <b-modal
      v-model="reportModalVisible"
      modal-class="actions-modal foryou-report-modal"
      centered
      hide-header
      hide-footer
      content-class="actions-modal-content"
    >
      <div v-if="reportTarget" class="report-sheet">
        <h3 class="report-title">
          {{ $t('Report {name}', { name: reportTarget.user_name }) }}
        </h3>
        <p class="report-label">{{ $t('Report reason') }}</p>
        <div class="report-reasons" role="radiogroup">
          <button
            v-for="reason in reportReasonTypes"
            :key="reason.value"
            type="button"
            role="radio"
            class="report-reason"
            :class="{ active: report.reason === reason.value }"
            :aria-checked="report.reason === reason.value ? 'true' : 'false'"
            @click="report.reason = reason.value"
          >
            {{ $t(reason.value) }}
          </button>
        </div>
        <textarea
          v-if="report.reason === 'Other'"
          v-model="report.description"
          class="report-textarea"
          rows="3"
          :placeholder="$t('Your message')"
        />
        <small v-if="reportError" class="report-error">{{ reportError }}</small>
        <div class="report-actions">
          <button type="button" class="report-btn report-btn--ghost" :disabled="reportLoading" @click="reportModalVisible = false">
            {{ $t('Cancel') }}
          </button>
          <button type="button" class="report-btn report-btn--primary" :disabled="reportLoading" @click="submitReport">
            {{ $t('Report') }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BAvatar, BModal } from 'bootstrap-vue'
import UserCardActions from '@/components/UserCardActions.vue'
import useJwt from '@/auth/jwt/useJwt'
import { throwDefaultError, showNotify } from '@/helpers/helpers'

const emptyImage = require('@/assets/images/pages/moments-empty.png')

export default {
  name: 'ForYouSwipeCards',
  components: {
    BAvatar,
    BModal,
    UserCardActions,
  },
  data() {
    return {
      emptyImage,
      users: [],
      currentIndex: 0,
      loading: false,
      offset: 0,
      limit: 12,
      allLoaded: false,
      // Sayfa her ekran boyutunda ızgara görünümünde (kart görünümü kaldırıldı)
      viewMode: 'grid',
      lastIsDesktop: window.innerWidth >= 768,
      actionModalVisible: false,
      selectedUserForAction: null,
      reportModalVisible: false,
      reportTarget: null,
      reportLoading: false,
      reportError: '',
      report: { reason: '', description: '' },
      // Swipe properties
      startX: 0,
      currentX: 0,
      isDragging: false,
      dragOffset: 0,
      cardWidth: 320,
      cardGap: 16,
      pauseIconTimeout: null,
    }
  },
  computed: {
    reportReasonTypes() {
      return this.$store.getters['app/reportReasonTypes'] || []
    },
    trackStyle() {
      const totalOffset = this.currentIndex * (this.cardWidth + this.cardGap)
      const dragPx = this.isDragging ? this.dragOffset : 0
      return {
        transform: `translateX(${-totalOffset + dragPx}px)`,
        transition: this.isDragging ? 'none' : 'transform 0.3s ease-out',
      }
    },
  },
  watch: {
    currentIndex(newIndex) {
      this.$nextTick(() => {
        this.handleVideoPlayback()
      })
      if (!this.loading && !this.allLoaded && newIndex >= this.users.length - 3) {
        this.loadMore()
      }
    },
  },
  created() {
    this.getUsers()
  },
  mounted() {
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('resize', this.onWindowResize)
    this.updateCardWidth()
    this.setupGridVideoObserver()
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('resize', this.onWindowResize)
    if (this.gridVideoObserver) this.gridVideoObserver.disconnect()
  },
  methods: {
    onWindowResize() {
      // Görünüm her ekranda ızgara; ekran boyutu değişince kart görünümüne geçilmez
      this.updateCardWidth()
    },
    updateCardWidth() {
      if (window.innerWidth >= 768) {
        this.cardWidth = 360
        this.cardGap = 20
      } else {
        this.cardWidth = Math.min(300, window.innerWidth * 0.8)
        this.cardGap = 12
      }
    },
    setViewMode(mode) {
      this.viewMode = mode
    },
    async getUsers(clear = true) {
      if (this.loading) return
      if (!clear && this.allLoaded) return

      if (clear) {
        this.users = []
        this.offset = 0
        this.currentIndex = 0
        this.allLoaded = false
      }

      this.loading = true
      try {
        const response = await useJwt.getPostsForYou(this.offset, this.limit)
        const newUsers = response.data.body || []

        const mapped = newUsers.map(u => ({
          ...u,
          mediaLoaded: false,
          isPlaying: false,
          showPauseIcon: false,
        }))

        if (clear) {
          this.users = mapped
        } else {
          this.users.push(...mapped)
        }

        this.offset += this.limit
        if (newUsers.length < this.limit) {
          this.allLoaded = true
        }
      } catch (error) {
        throwDefaultError(this.$toast, error)
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (this.loading || this.allLoaded) return
      await this.getUsers(false)
    },
    // Izgaradaki videolar otomatik oynar (sessiz, döngü). Önceden yalnızca fare üstüne gelince oynuyordu ve
    // mobilde hiç oynamıyordu. Performans için yalnızca ekranda görünen videolar oynatılır, çıkanlar durdurulur.
    onGridVideoLoaded(user, event) {
      this.onMediaLoad(user)
      const video = event && event.target
      if (!video) return
      // Bazı tarayıcılar muted özelliğini Vue'nun bağladığı attribute'tan almıyor; otomatik oynatma için şart
      video.muted = true
      if (this.gridVideoObserver) {
        this.gridVideoObserver.observe(video)
      } else {
        video.play().catch(() => {})
      }
    },
    setupGridVideoObserver() {
      if (typeof window.IntersectionObserver !== 'function') return
      this.gridVideoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target
          if (entry.isIntersecting) {
            video.muted = true
            video.play().catch(() => {})
          } else {
            video.pause()
          }
        })
      }, { threshold: 0.25 })
    },
    openUserActionMenu(user) {
      this.selectedUserForAction = user
      this.actionModalVisible = true
    },
    copyUserLink(user) {
      if (!user) return
      const profileUrl = `${window.location.origin}/profile/${user.uuid}`
      if (navigator.clipboard) {
        navigator.clipboard.writeText(profileUrl)
      }
      showNotify(this.$toast, this.$t('Link copied to clipboard') || 'Profil bağlantısı kopyalandı', 'success')
      this.actionModalVisible = false
    },
    blockUser(user) {
      if (!user) return
      this.actionModalVisible = false
      useJwt.blockUser(user.uuid)
        .then(() => {
          showNotify(this.$toast, this.$t('User blocked successfully') || 'Kullanıcı engellendi', 'success')
          this.users = this.users.filter(u => u.uuid !== user.uuid)
        })
        .catch(err => {
          throwDefaultError(this.$toast, err)
        })
    },
    openReport(user) {
      if (!user) return
      this.actionModalVisible = false
      this.reportTarget = user
      this.report = { reason: '', description: '' }
      this.reportError = ''
      this.reportModalVisible = true
    },
    submitReport() {
      const { reason, description } = this.report
      if (!reason) {
        this.reportError = this.$t('Report reason')
        return
      }
      if (reason === 'Other' && !description.trim()) {
        this.reportError = this.$t('Your message')
        return
      }
      this.reportError = ''
      this.reportLoading = true
      useJwt.reportUser2({
        related_id: null,
        reason: reason === 'Other' ? description.trim() : reason,
        report_source: 'for_you',
        reported_uuid: this.reportTarget.uuid,
      })
        .then(() => {
          showNotify(this.$toast, this.$t('Reporting user successful'), 'success')
          this.reportModalVisible = false
          this.reportTarget = null
        })
        .catch(err => {
          throwDefaultError(this.$toast, err)
        })
        .finally(() => {
          this.reportLoading = false
        })
    },
    goToProfile(user) {
      this.actionModalVisible = false
      this.$router.push({
        name: 'users.show',
        params: { id: user.uuid },
      })
    },
    goToChat(user) {
      this.actionModalVisible = false
      this.$router.push({
        name: 'chat',
        params: { userId: user.uuid },
      })
    },
    goToDiscover() {
      this.$emit('switch-tab', 'discoverygrid')
    },
    // Swipe
    onTouchStart(e) {
      this.startX = e.touches[0].clientX
      this.currentX = this.startX
      this.isDragging = true
      this.dragOffset = 0
    },
    onTouchMove(e) {
      if (!this.isDragging) return
      this.currentX = e.touches[0].clientX
      this.dragOffset = this.currentX - this.startX
    },
    onTouchEnd() {
      this.finishSwipe()
    },
    onMouseDown(e) {
      e.preventDefault()
      this.startX = e.clientX
      this.currentX = this.startX
      this.isDragging = true
      this.dragOffset = 0
    },
    onMouseMove(e) {
      if (!this.isDragging) return
      this.currentX = e.clientX
      this.dragOffset = this.currentX - this.startX
    },
    onMouseUp() {
      this.finishSwipe()
    },
    finishSwipe() {
      if (!this.isDragging) return
      this.isDragging = false

      const threshold = 50
      if (this.dragOffset < -threshold) {
        this.nextCard()
      } else if (this.dragOffset > threshold) {
        this.prevCard()
      }
      this.dragOffset = 0
    },
    prevCard() {
      if (this.users.length === 0) return
      if (this.currentIndex > 0) {
        this.currentIndex--
      } else if (this.users.length > 0) {
        this.currentIndex = this.users.length - 1
      }
    },
    nextCard() {
      if (this.users.length === 0) return
      if (this.currentIndex < this.users.length - 1) {
        this.currentIndex++
      } else if (!this.allLoaded) {
        this.loadMore()
      } else {
        this.currentIndex = 0
      }
    },
    handleVideoPlayback() {
      if (this.pauseIconTimeout) {
        clearTimeout(this.pauseIconTimeout)
        this.pauseIconTimeout = null
      }
      this.users.forEach((user, idx) => {
        if (user.media_type === 'video') {
          const videoRef = this.$refs[`video-${idx}`]
          const video = Array.isArray(videoRef) ? videoRef[0] : videoRef
          if (video) {
            video.pause()
            video.currentTime = 0
          }
          this.$set(user, 'isPlaying', false)
          this.$set(user, 'showPauseIcon', false)
        }
      })
    },
    toggleVideo(idx) {
      if (idx !== this.currentIndex) return
      const user = this.users[idx]
      if (!user || user.media_type !== 'video') return

      const videoRef = this.$refs[`video-${idx}`]
      const video = Array.isArray(videoRef) ? videoRef[0] : videoRef
      if (!video) return

      if (this.pauseIconTimeout) {
        clearTimeout(this.pauseIconTimeout)
        this.pauseIconTimeout = null
      }

      if (user.isPlaying) {
        video.pause()
        this.$set(user, 'isPlaying', false)
        this.$set(user, 'showPauseIcon', true)
        this.pauseIconTimeout = setTimeout(() => {
          this.$set(user, 'showPauseIcon', false)
        }, 500)
      } else {
        video.muted = false
        video.play().catch(() => {})
        this.$set(user, 'isPlaying', true)
        this.$set(user, 'showPauseIcon', false)
      }
    },
    onMediaLoad(user) {
      this.$set(user, 'mediaLoaded', true)
    },
  },
}
</script>

<style lang="scss" scoped>
.foryou-container {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 24px 60px 24px;
  background: transparent !important;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px 12px 90px 12px;
  }
}

/* Header & View Mode Switcher */
.foryou-header {
  width: 100%;
  max-width: 1300px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 20px 24px;
  min-height: 100px;
  position: relative;
  overflow: visible;
  /* Başlığın arkasındaki kutu kaldırıldı (arka plandaki kalpler görünür kalsın) */
  border-radius: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
  flex-wrap: wrap;
  gap: 16px;
  z-index: 5;

  .foryou-title-block,
  .view-mode-toggle {
    position: relative;
    z-index: 2;
  }

  .foryou-title-block {
    .foryou-title {
      color: #fff;
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 6px 0;
      display: flex;
      align-items: center;
      gap: 8px;
      letter-spacing: -0.3px;

      .sparkle-icon {
        font-size: 22px;
      }
    }

    .foryou-subtitle {
      color: rgba(255, 255, 255, 0.65);
      font-size: 14px;
      margin: 0;
    }
  }

  .view-mode-toggle {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 30px;
    padding: 4px;
    backdrop-filter: blur(12px);

    .mode-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 7px 16px;
      border-radius: 24px;
      border: none;
      background: transparent;
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.25s ease;

      &.active {
        background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
        color: #fff;
        box-shadow: 0 4px 14px rgba(236, 72, 153, 0.35);
      }

      &:hover:not(.active) {
        color: #fff;
        background: rgba(255, 255, 255, 0.06);
      }
    }
  }
}

/* =========================================
   1. GRID VIEW STYLES
   ========================================= */
.foryou-grid-wrapper {
  width: 100%;
  max-width: 1300px;
  display: flex;
  flex-direction: column;
}

.foryou-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 22px;
  width: 100%;

  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;
  }
}

.grid-card-item {
  width: 100%;
  height: 390px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
  background: #161e38;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.3s ease;

  @media (max-width: 576px) {
    height: 290px;
    border-radius: 16px;
  }

  &:hover {
    transform: translateY(-6px) scale(1.015);
    border-color: rgba(236, 72, 153, 0.7);
    box-shadow: 0 16px 36px rgba(168, 85, 247, 0.35), 0 0 20px rgba(236, 72, 153, 0.2);
  }

  .grid-card-inner {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .grid-card-media-wrapper {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: #120622;

    .grid-card-media {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.4s ease;
    }

    &:hover .grid-card-media {
      transform: scale(1.04);
    }
  }

  /* Header overlay */
  .grid-card-header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 14px;
    background: linear-gradient(180deg, rgba(10, 4, 20, 0.85) 0%, rgba(10, 4, 20, 0.3) 70%, transparent 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 5;

    @media (max-width: 576px) {
      padding: 10px;
    }

    .user-meta {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      min-width: 0;
      // Menü (⋮) düğmesine yer bırakarak daralabilsin
      flex: 1 1 auto;
      margin-right: 8px;

      .user-avatar {
        border: 2px solid #a855f7;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
      }

      .user-text {
        display: flex;
        flex-direction: column;
        min-width: 0;

        .user-name-row {
          min-width: 0;
          overflow: hidden;
        }

        // Satır içi (inline) öğede "…" kısaltması çalışmıyordu; uzun isimler ⋮ düğmesinin altına giriyordu
        .user-name {
          display: block;
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
        }
      }
    }

    .card-options-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      backdrop-filter: blur(8px);
      transition: all 0.2s ease;
      flex-shrink: 0;

      &:hover {
        background: rgba(168, 85, 247, 0.8);
        color: #fff;
        transform: scale(1.1);
      }
    }
  }

  /* Footer overlay */
  .grid-card-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 24px 14px 14px 14px;
    background: linear-gradient(0deg, rgba(8, 2, 16, 0.95) 0%, rgba(8, 2, 16, 0.6) 70%, transparent 100%);
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 5;

    @media (max-width: 576px) {
      padding: 16px 8px 10px 8px;
      gap: 6px;
    }

    .grid-message-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 16px;
      background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
      color: #fff;
      font-size: 14px;
      font-weight: 700;
      border: none;
      border-radius: 25px;
      cursor: pointer;
      box-shadow: 0 4px 15px rgba(236, 72, 153, 0.4);
      transition: all 0.25s ease;

      @media (max-width: 576px) {
        font-size: 12px;
        padding: 8px 10px;
        gap: 4px;
      }

      &:hover {
        background: linear-gradient(135deg, #f43f5e 0%, #ec4899 100%);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(236, 72, 153, 0.6);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .grid-profile-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.12);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      backdrop-filter: blur(8px);
      transition: all 0.2s ease;
      flex-shrink: 0;

      @media (max-width: 576px) {
        width: 34px;
        height: 34px;
      }

      &:hover {
        background: rgba(168, 85, 247, 0.6);
        border-color: #a855f7;
        transform: scale(1.08);
      }
    }
  }
}

.load-more-section {
  display: flex;
  justify-content: center;
  padding: 36px 0 20px 0;

  .load-more-btn {
    padding: 12px 36px;
    background: rgba(168, 85, 247, 0.18);
    border: 1.5px solid rgba(168, 85, 247, 0.4);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    border-radius: 30px;
    cursor: pointer;
    backdrop-filter: blur(10px);
    transition: all 0.25s ease;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
      border-color: transparent;
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(168, 85, 247, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/* =========================================
   2. CARDS / SWIPER VIEW STYLES
   ========================================= */
.cards-viewport-wrapper {
  width: 100%;
  max-width: 900px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 10px 0 30px 0;

  .card-nav-arrow {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: rgba(25, 12, 45, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(10px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
    z-index: 20;
    transition: all 0.25s ease;
    flex-shrink: 0;

    @media (max-width: 768px) {
      display: none;
    }

    &:hover {
      background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
      border-color: transparent;
      transform: scale(1.12);
      box-shadow: 0 8px 25px rgba(236, 72, 153, 0.5);
    }

    &:active {
      transform: scale(0.96);
    }
  }

  .arrow-left {
    margin-right: 20px;
  }

  .arrow-right {
    margin-left: 20px;
  }
}

.cards-viewport {
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  user-select: none;
  cursor: grab;
  position: relative;
  display: flex;
  align-items: center;
  padding: 20px 0;

  &:active {
    cursor: grabbing;
  }
}

.cards-track {
  display: flex;
  gap: 16px;
  padding-left: calc(50% - min(180px, 40vw));
  padding-right: calc(50% - min(180px, 40vw));
  will-change: transform;
}

.card-slide {
  flex-shrink: 0;
  width: min(360px, 80vw);
  height: min(520px, 68vh);
  transform: scale(0.88);
  opacity: 0.55;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease;

  &.is-active {
    transform: scale(1);
    opacity: 1;
  }
}

.user-card {
  width: 100%;
  height: 100%;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(180deg, #161e38 0%, #0f1426 100%);
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(168, 85, 247, 0.2);
}

.card-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  z-index: 15;
  backdrop-filter: blur(6px);
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    transform: scale(1.1);
  }
}

.card-options-top {
  position: absolute;
  top: 14px;
  right: 54px;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  z-index: 15;
  backdrop-filter: blur(6px);
  transition: all 0.2s;

  &:hover {
    background: rgba(168, 85, 247, 0.8);
    color: #fff;
    transform: scale(1.1);
  }
}

.card-header {
  position: absolute;
  top: 14px;
  left: 14px;
  right: 95px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
  cursor: pointer;
}

.user-avatar {
  flex-shrink: 0;
  border: 2px solid #fff;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
}

.content-type-badge {
  display: inline-block;
  width: fit-content;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.ai {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(168, 85, 247, 0.9));
    color: #fff;
  }

  &.real {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.9), rgba(74, 222, 128, 0.9));
    color: #fff;
  }
}

.card-image-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.media-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #2d1b4e 0%, #1a0f30 100%);
  overflow: hidden;

  .shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(139, 92, 246, 0.15) 50%,
      transparent 100%
    );
    animation: shimmer 1.5s infinite;
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;

  &.card-video {
    pointer-events: auto;
    cursor: pointer;
  }
}

.video-play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  z-index: 5;
  cursor: pointer;
  transition: opacity 0.3s, transform 0.2s;
  opacity: 0;
  pointer-events: none;

  &.is-visible {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }
}

.message-btn {
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ec4899, #f472b6);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 12px 42px;
  border-radius: 30px;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 20px rgba(236, 72, 153, 0.5);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.25s ease;

  &:hover {
    transform: translateX(-50%) scale(1.05);
    background: linear-gradient(135deg, #f43f5e, #ec4899);
    box-shadow: 0 6px 24px rgba(236, 72, 153, 0.7);
  }

  &:active {
    transform: translateX(-50%) scale(0.98);
  }
}

/* Empty State */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #fff;
  text-align: center;
  padding: 40px 20px;
}

.empty-image {
  max-width: 260px;
  width: 100%;
  height: auto;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
}

.empty-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 24px 0;
}

.discover-btn {
  background: linear-gradient(135deg, #ec4899, #f472b6);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 60px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(236, 72, 153, 0.4);
  transition: transform 0.2s;
  margin-bottom: 24px;

  &:hover {
    transform: scale(1.05);
  }
}

.create-post-btn {
  cursor: pointer;

  .outer-ring {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(173, 50, 238, 0.19);

    .inner-ring {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        font-size: 18px;
        color: #fff;
      }
    }
  }
}

/* Actions Modal */
::v-deep .actions-modal .modal-content {
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(180deg, #1f1035 0%, #120822 100%) !important;
  border: 1px solid rgba(168, 85, 247, 0.3) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8) !important;
}

.actions-list {
  .action-item {
    padding: 16px 20px;
    text-align: center;
    font-size: 15px;
    font-weight: 500;
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;

    .action-icon {
      font-size: 16px;
    }

    &:hover {
      background: rgba(168, 85, 247, 0.2);
    }

    &:last-child {
      border-bottom: none;
    }

    &.cancel {
      color: rgba(255, 255, 255, 0.7);
      &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
      }
    }

    &.text-danger {
      color: #ff453a !important;
      &:hover {
        background: rgba(255, 69, 58, 0.15);
      }
    }
  }
}


/* =========================================================
   Butonlar — uygulamanın tasarım diline uyarlandı (sadece görünüm)
   ========================================================= */
.foryou-container {
  .foryou-header {
    padding: 8px 4px;
    min-height: 0;

    .view-mode-toggle {
      background: rgba(32, 22, 72, 0.72);
      border: 1px solid rgba(150, 110, 255, 0.28);

      .mode-btn.active {
        background: linear-gradient(135deg, rgba(234, 3, 178, 0.55) 0%, rgba(168, 85, 247, 0.45) 50%, rgba(99, 102, 241, 0.3) 100%);
        box-shadow: 0 2px 14px rgba(234, 3, 178, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.25);
      }
    }
  }

  /* Grid kartı alt butonları */
  .grid-message-btn {
    border: 0 !important;
    border-radius: 9999px !important;
    color: #fff !important;
    font-weight: 700 !important;
    background: linear-gradient(135deg, #ff1f8f, #7c3aed) !important;
    box-shadow: 0 6px 16px rgba(255, 31, 143, 0.35) !important;
    transition: filter .15s ease, transform .15s ease !important;

    &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  }

  .grid-profile-btn,
  .card-options-btn {
    border-radius: 50% !important;
    color: #fff !important;
    background: rgba(14, 10, 40, 0.6) !important;
    border: 1px solid rgba(170, 140, 255, 0.4) !important;
    backdrop-filter: blur(6px);

    &:hover { background: rgba(139, 92, 246, 0.35) !important; }
  }

  .load-more-btn {
    border-radius: 9999px !important;
    color: #fff !important;
    font-weight: 700 !important;
    background: linear-gradient(135deg, rgba(234, 3, 178, 0.28) 0%, rgba(168, 85, 247, 0.22) 50%, rgba(99, 102, 241, 0.12) 100%) !important;
    border: 1px solid rgba(234, 3, 178, 0.35) !important;
    box-shadow: 0 2px 14px rgba(234, 3, 178, 0.18) !important;

    &:hover:not(:disabled) { filter: brightness(1.2); }
  }

  /* Kart görünümü */
  .card-nav-arrow {
    background: rgba(14, 10, 40, 0.65) !important;
    border: 1px solid rgba(170, 140, 255, 0.4) !important;
    color: #fff !important;
    backdrop-filter: blur(6px);

    &:hover { background: rgba(139, 92, 246, 0.35) !important; }
  }

  .card-close {
    background: rgba(14, 10, 40, 0.65) !important;
    border: 1px solid rgba(170, 140, 255, 0.4) !important;
    color: #fff !important;
  }

  .message-btn {
    background: linear-gradient(135deg, #ff1f8f, #7c3aed) !important;
    box-shadow: 0 8px 22px rgba(255, 31, 143, 0.45) !important;
    border: 1px solid rgba(255, 255, 255, 0.18);

    &:hover { background: linear-gradient(135deg, #ff3ea0, #8b5cf6) !important; }
  }

  .discover-btn {
    background: linear-gradient(135deg, #ff1f8f, #7c3aed) !important;
    box-shadow: 0 8px 22px rgba(255, 31, 143, 0.4) !important;
  }
}
</style>

<style lang="scss">
/* Seçenekler / şikayet pencereleri body'ye taşındığı için (portal) koyu tema burada, kapsamsız tanımlanır.
   Önceden scoped tanım pencereye ulaşmıyordu: beyaz zemin üstünde beyaz yazı yüzünden menü boş görünüyordu. */
.actions-modal .modal-content,
.actions-modal .actions-modal-content {
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(180deg, #1f1035 0%, #120822 100%) !important;
  border: 1px solid rgba(168, 85, 247, 0.3) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8) !important;
  color: #fff;
}

.actions-modal .modal-body {
  padding: 0;
}

.actions-modal .actions-list .action-item.text-warning {
  color: #fbbf24 !important;
}

.foryou-report-modal {
  .report-sheet {
    padding: 22px 20px 20px;
  }

  .report-title {
    margin: 0 0 14px;
    color: #fff !important;
    font-size: 18px;
    font-weight: 800;
    text-align: center;
  }

  .report-label {
    margin: 0 0 8px;
    color: rgba(236, 232, 255, 0.7);
    font-size: 13px;
  }

  .report-reasons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .report-reason {
    width: 100%;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid rgba(168, 85, 247, 0.3);
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    text-align: start;
    cursor: pointer;

    &.active {
      border-color: transparent;
      background: linear-gradient(135deg, rgba(124, 58, 237, 0.8), rgba(234, 3, 178, 0.8));
    }
  }

  .report-textarea {
    width: 100%;
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid rgba(168, 85, 247, 0.35);
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 14px;
    resize: vertical;
  }

  .report-error {
    display: block;
    margin-top: 8px;
    color: #f87171;
  }

  .report-actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
  }

  .report-btn {
    flex: 1;
    height: 44px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
    }
  }

  .report-btn--ghost {
    border: 1px solid rgba(168, 85, 247, 0.4);
    background: transparent;
    color: #fff;
  }

  .report-btn--primary {
    border: 0;
    color: #fff;
    background: linear-gradient(135deg, #7c3aed, #ea03b2);
  }
}
</style>
